import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import {
  listVacanciesApi,
  getVacancyApi,
  setVacancyActiveApi,
  deleteVacancyApi,
  listApplicationsApi,
  VacancyItem,
  VacancyStatus,
  Application,
} from "../../features/vacancies/api/vacancies.api"
import {
  BriefcaseIcon,
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClockIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  ChevronLeftIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

const LIMIT = 20
const APP_LIMIT = 20

/* ── Status config ── */
const STATUS_CONFIG: Record<VacancyStatus, { label: string; className: string }> = {
  active:   { label: "Активна",   className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  inactive: { label: "Неактивна", className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200" },
  draft:    { label: "Черновик", className: "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200" },
}

const HEADER_COLOR: Record<VacancyStatus, string> = {
  active:   "from-emerald-500 to-green-400",
  inactive: "from-gray-400 to-gray-300",
  draft:    "from-yellow-500 to-amber-400",
}

const StatusBadge = ({ status }: { status: VacancyStatus }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: "bg-gray-100 text-gray-500" }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const formatDate = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })

const formatSalary = (item: VacancyItem) => {
  if (!item.salary_to) return "—"
  return `${item.salary_to.toLocaleString()} ${item.salary_currency ?? ""}`
}

export const VacanciesPage = () => {
  const [items, setItems] = useState<VacancyItem[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)

  const [filterStatus, setFilterStatus] = useState("")
  const [filterFormat, setFilterFormat] = useState("")

  const [selected, setSelected] = useState<VacancyItem | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMsg, setActionMsg] = useState<{ text: string; ok: boolean } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  /* Applications tab */
  const [tab, setTab] = useState<"info" | "apps">("info")
  const [apps, setApps] = useState<Application[]>([])
  const [appsTotal, setAppsTotal] = useState(0)
  const [appsPage, setAppsPage] = useState(1)
  const [appsLoading, setAppsLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    const offset = (page - 1) * LIMIT
    const res = await listVacanciesApi({
      limit: LIMIT,
      offset,
      status: filterStatus || undefined,
      work_format: filterFormat || undefined,
    })
    setItems(res.data.items)
    setTotal(res.data.total)
    setLoading(false)
  }

  useEffect(() => { load() }, [page, filterStatus, filterFormat]) // eslint-disable-line
  useEffect(() => { setPage(1) }, [filterStatus, filterFormat])

  const openDrawer = async (v: VacancyItem) => {
    setSelected(v)
    setTab("info")
    setActionMsg(null)
    setConfirmDelete(false)
    setApps([])
    setAppsPage(1)
    setAppsTotal(0)
  }

  const loadApps = async (vacancyId: string, p: number) => {
    setAppsLoading(true)
    const res = await listApplicationsApi(vacancyId, APP_LIMIT, (p - 1) * APP_LIMIT)
    setApps(res.data.items)
    setAppsTotal(res.data.total)
    setAppsLoading(false)
  }

  useEffect(() => {
    if (tab === "apps" && selected) loadApps(selected.id, appsPage)
  }, [tab, appsPage, selected]) // eslint-disable-line

  const handleToggleActive = async () => {
    if (!selected) return
    setActionLoading(true)
    try {
      await setVacancyActiveApi(selected.id, !selected.is_active)
      const updated = { ...selected, is_active: !selected.is_active, status: (!selected.is_active ? "active" : "inactive") as VacancyStatus }
      setSelected(updated)
      setItems((prev) => prev.map((v) => (v.id === selected.id ? updated : v)))
      setActionMsg({ text: updated.is_active ? "Вакансия активирована" : "Вакансия деактивирована", ok: true })
    } catch {
      setActionMsg({ text: "Ошибка действия", ok: false })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    setActionLoading(true)
    try {
      await deleteVacancyApi(selected.id)
      setItems((prev) => prev.filter((v) => v.id !== selected.id))
      setTotal((t) => t - 1)
      setSelected(null)
    } catch {
      setActionMsg({ text: "Ошибка удаления", ok: false })
      setConfirmDelete(false)
    } finally {
      setActionLoading(false)
    }
  }

  const totalPages = Math.ceil(total / LIMIT)
  const appsTotalPages = Math.ceil(appsTotal / APP_LIMIT)

  return (
    <DashboardLayout title="Вакансии">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b bg-gray-50/60">
          <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

          <FilterDropdown
            label="Статус"
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: "active", label: "Активна", dot: "bg-emerald-400" },
              { value: "inactive", label: "Неактивна", dot: "bg-gray-400" },
              { value: "draft", label: "Черновик", dot: "bg-yellow-400" },
            ]}
          />

          <FilterDropdown
            label="Формат"
            value={filterFormat}
            onChange={setFilterFormat}
            options={[
              { value: "remote", label: "Удалённо" },
              { value: "office", label: "Офис" },
              { value: "hybrid", label: "Гибрид" },
            ]}
          />

          {(filterStatus || filterFormat) && (
            <button
              type="button"
              onClick={() => { setFilterStatus(""); setFilterFormat("") }}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Сбросить фильтры
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">
            {!loading && `${total} вакансий`}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Название</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Зарплата</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Формат</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Просмотры</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                <th className="px-5 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                      <td key={j} className="px-5 py-4">
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-24" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center">
                    <BriefcaseIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Вакансий не найдено</p>
                  </td>
                </tr>
              ) : (
                items.map((v) => (
                  <tr key={v.id} onClick={() => openDrawer(v)} className="hover:bg-gray-50/60 transition-colors cursor-pointer group">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <BriefcaseIcon className="w-4 h-4 text-blue-500" />
                        </div>
                        <span className="font-medium text-gray-800 truncate max-w-[200px]">{v.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{formatSalary(v)}</td>
                    <td className="px-5 py-3.5">
                      {v.work_format ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full capitalize">
                          <GlobeAltIcon className="w-3 h-3" />
                          {v.work_format}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        <EyeIcon className="w-3.5 h-3.5 text-gray-400" />
                        {v.views_count}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={v.status} />
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">{formatDate(v.created_at)}</td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); openDrawer(v) }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                      >
                        <EyeIcon className="w-3.5 h-3.5" />
                        Открыть
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages || 1}</span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Назад
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${selected ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setSelected(null)}
          />
          <div
            className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${selected ? "translate-x-0" : "translate-x-full"}`}
          >
            {selected && (
              <>
                {/* Header */}
                <div className={`bg-gradient-to-r ${HEADER_COLOR[selected.status]} px-6 pt-6 pb-8 flex-shrink-0 relative`}>
                  <button
                    onClick={() => setSelected(null)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 text-white" />
                  </button>

                  <div className="flex items-center gap-2 mb-3">
                    <BriefcaseIcon className="w-5 h-5 text-white/80" />
                    <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Вакансия</span>
                  </div>

                  <p className="text-white font-semibold text-lg leading-snug pr-10">{selected.title}</p>

                  <div className="flex items-center gap-2 mt-3">
                    <StatusBadge status={selected.status} />
                    {selected.work_format && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white capitalize">
                        <GlobeAltIcon className="w-3 h-3" />
                        {selected.work_format}
                      </span>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b bg-white flex-shrink-0">
                  {([
                    { key: "info", label: "Информация", icon: <BriefcaseIcon className="w-3.5 h-3.5" /> },
                    { key: "apps", label: "Отклики", icon: <UsersIcon className="w-3.5 h-3.5" /> },
                  ] as const).map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                        tab === t.key
                          ? "border-blue-600 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {t.icon}
                      {t.label}
                      {t.key === "apps" && appsTotal > 0 && (
                        <span className="bg-blue-100 text-blue-600 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                          {appsTotal}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">

                  {/* ── INFO TAB ── */}
                  {tab === "info" && (
                    <div className="space-y-5">

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <StatCard
                          icon={<CurrencyDollarIcon className="w-4 h-4 text-emerald-500" />}
                          label="Зарплата"
                          value={formatSalary(selected)}
                          color="bg-emerald-50"
                        />
                        <StatCard
                          icon={<EyeIcon className="w-4 h-4 text-blue-500" />}
                          label="Просмотры"
                          value={String(selected.views_count)}
                          color="bg-blue-50"
                        />
                        <StatCard
                          icon={<ClockIcon className="w-4 h-4 text-amber-500" />}
                          label="Добавлена"
                          value={formatDate(selected.created_at)}
                          color="bg-amber-50"
                        />
                      </div>

                      {/* Details */}
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Детали</p>
                        <Row label="ID" value={selected.id} mono />
                        <Row label="ID создателя" value={selected.creator_id} mono />
                        {selected.employment_type && (
                          <Row label="Тип занятости" value={selected.employment_type} />
                        )}
                        {selected.work_format && (
                          <Row label="Формат" value={selected.work_format} />
                        )}
                      </div>

                      {/* Actions */}
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Действия</p>

                        {selected.is_active ? (
                          <button
                            disabled={actionLoading}
                            onClick={handleToggleActive}
                            className="w-full flex items-center justify-center gap-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 disabled:opacity-40 text-orange-700 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                          >
                            <XCircleIcon className="w-4 h-4" />
                            Деактивировать вакансию
                          </button>
                        ) : (
                          <button
                            disabled={actionLoading}
                            onClick={handleToggleActive}
                            className="w-full flex items-center justify-center gap-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 disabled:opacity-40 text-emerald-700 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                            Активировать вакансию
                          </button>
                        )}

                        {!confirmDelete ? (
                          <button
                            disabled={actionLoading}
                            onClick={() => setConfirmDelete(true)}
                            className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-40 text-red-700 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                          >
                            <TrashIcon className="w-4 h-4" />
                            Удалить вакансию
                          </button>
                        ) : (
                          <div className="border border-red-200 rounded-xl p-4 bg-red-50 space-y-3">
                            <p className="text-sm text-red-700 font-medium">Подтвердите удаление</p>
                            <p className="text-xs text-red-500">Это действие нельзя отменить. Вакансия будет удалена навсегда.</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setConfirmDelete(false)}
                                className="flex-1 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors"
                              >
                                Отмена
                              </button>
                              <button
                                disabled={actionLoading}
                                onClick={handleDelete}
                                className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                              >
                                {actionLoading
                                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  : <TrashIcon className="w-4 h-4" />
                                }
                                Удалить
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Feedback */}
                      {actionMsg && (
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${actionMsg.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                          {actionMsg.ok
                            ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                            : <XCircleIcon className="w-4 h-4 flex-shrink-0" />
                          }
                          {actionMsg.text}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── APPS TAB ── */}
                  {tab === "apps" && (
                    <div className="space-y-3">
                      {appsLoading ? (
                        Array.from({ length: 4 }).map((_, i) => (
                          <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
                        ))
                      ) : apps.length === 0 ? (
                        <div className="py-16 text-center">
                          <UsersIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">Откликов пока нет</p>
                        </div>
                      ) : (
                        <>
                          {apps.map((app, i) => (
                            <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                              <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                                <UsersIcon className="w-4 h-4 text-violet-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-xs text-gray-600 truncate">{app.applicant_id}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{formatDate(app.created_at)}</p>
                              </div>
                            </div>
                          ))}

                          {appsTotalPages > 1 && (
                            <div className="flex items-center justify-between pt-2">
                              <span className="text-xs text-gray-400">
                                Стр. {appsPage} / {appsTotalPages}
                              </span>
                              <div className="flex gap-1.5">
                                <button
                                  disabled={appsPage === 1}
                                  onClick={() => setAppsPage((p) => p - 1)}
                                  className="p-1.5 border rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                                >
                                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  disabled={appsPage >= appsTotalPages}
                                  onClick={() => setAppsPage((p) => p + 1)}
                                  className="p-1.5 border rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors rotate-180"
                                >
                                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80">
                  <button
                    onClick={() => setSelected(null)}
                    className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors"
                  >
                    Закрыть
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}

/* ── Helpers ── */
const StatCard = ({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode
  label: string
  value: string
  color: string
}) => (
  <div className={`${color} rounded-xl p-3 flex flex-col gap-1.5`}>
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-sm font-semibold text-gray-800 truncate">{value}</p>
  </div>
)

const Row = ({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
    <span className={`text-xs text-gray-600 truncate ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
)
